import { useCampaignLaunchState, type LaunchableCampaign } from '@/hooks/useCampaignLaunchState';
import {
  STEPS,
  isStepReachable,
  isStepSatisfied,
  stepIndex,
  type StepGates,
  type StepId,
} from '@/lib/campaignSteps';
import CampaignStepper from './CampaignStepper';
import DetailsStep, { type CampaignDetails } from './DetailsStep';
import ChannelStep from './ChannelStep';
import MediaStep from './MediaStep';
import AudienceStep from './AudienceStep';
import FinalPreviewStep from './FinalPreviewStep';
import LaunchPreview from './LaunchPreview';

export interface WizardCampaign extends LaunchableCampaign {
  campaignMediaId: string | null;
  description?: string | null;
  status: string;
}

interface CampaignSetupWizardProps<C extends WizardCampaign> {
  campaign: C;
  workspaceId: string;
  step: Exclude<StepId, 'results'>;
  onStepChange: (step: StepId) => void;
  /** A replacement campaign (media refetch) or a partial update (details saved). */
  onCampaignChange: (next: C | Partial<C>) => void;
  onLaunched: (campaign: { id: string; status: string }) => void;
  onExit: () => void;
}

/**
 * Steps 1–5 for a campaign that exists and has not been launched.
 *
 * This component owns the launch state (see useCampaignLaunchState) and stays mounted across those
 * steps, so moving between them never loses what was entered. Only the step's own panel swaps.
 */
export default function CampaignSetupWizard<C extends WizardCampaign>({
  campaign, workspaceId, step, onStepChange, onCampaignChange, onLaunched, onExit,
}: CampaignSetupWizardProps<C>) {
  const state = useCampaignLaunchState({
    campaign,
    workspaceId,
    onLaunched,
    // The hook switches the campaign's media itself — from the media step, and when a template made
    // from generated media brings its own — and hands back the refetched campaign.
    onCampaignChange: (next) => onCampaignChange(next as C),
  });

  const gates: StepGates = {
    created: true,
    channelValid: state.channelValid,
    mediaValid: state.mediaValid,
    audienceValid: state.audienceValid,
    launched: false,
  };

  // Every move writes the pending autosave first, so a reload straight after Next restores what was
  // just chosen rather than what was saved half a second earlier.
  const goTo = (next: StepId) => {
    state.flush();
    onStepChange(next);
  };

  const index = stepIndex(step);
  const back = () => goTo(STEPS[index - 1].id);
  const next = () => goTo(STEPS[index + 1].id);

  const showPreview = step !== 'details';

  const panel = (() => {
    switch (step) {
      case 'details':
        return (
          <DetailsStep
            workspaceId={workspaceId}
            campaign={campaign}
            onSaved={(saved: CampaignDetails) => {
              onCampaignChange({ name: saved.name, description: saved.description } as Partial<C>);
              next();
            }}
            onCancel={onExit}
          />
        );
      case 'channel':
        return <ChannelStep state={state} onBack={back} onNext={next} />;
      case 'media':
        return (
          <MediaStep<C>
            workspaceId={workspaceId}
            campaign={campaign}
            state={state}
            onBack={back}
            onNext={next}
            onChooseTemplate={() => goTo('channel')}
          />
        );
      case 'audience':
        return <AudienceStep state={state} onBack={back} onNext={next} />;
      case 'launch':
        return <FinalPreviewStep state={state} campaign={campaign} onBack={back} onEdit={goTo} />;
      default:
        return null;
    }
  })();

  return (
    <>
      <CampaignStepper
        current={step}
        // Earlier steps are always reachable, whatever their gate says: going back is how a gate
        // gets satisfied.
        isReachable={(id) => stepIndex(id) < index || isStepReachable(id, gates)}
        isComplete={(id) => stepIndex(id) < index && isStepSatisfied(id, gates)}
        onSelect={goTo}
      />

      {showPreview ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
          {panel}
          <LaunchPreview
            state={state}
            onChangeMedia={step === 'media' ? undefined : () => goTo('media')}
          />
        </div>
      ) : panel}
    </>
  );
}
